import axios from "axios";

// Get base URL from environment variables
const baseURL = import.meta.env.VITE_REACT_APP_API_URL;

const instance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor to add token to headers
// Using a function to get store to avoid circular dependency
let getStore: (() => any) | null = null;
let getPersistor: (() => any) | null = null;
let logoutAction: (() => any) | null = null;

export const setStoreGetter = (storeGetter: () => any) => {
  getStore = storeGetter;
};

export const setPersistorGetter = (persistorGetter: () => any) => {
  getPersistor = persistorGetter;
};

export const setLogoutAction = (action: () => any) => {
  logoutAction = action;
};

instance.interceptors.request.use(
  (config) => {
    if (getStore) {
      try {
        const store = getStore();
        const state = store.getState();
        const token = state.auth?.access_token;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Silently fail if store is not available
        console.warn("Could not get token from store:", error);
      }
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Flag to prevent multiple logout calls
let isLoggingOut = false;

// Add response interceptor for handling errors and 401 unauthorized
instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    console.error("Axios Response Error:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
    });

    // Handle 401 Unauthorized - clear auth and redirect to login
    if (error.response?.status === 401 && !isLoggingOut) {
      isLoggingOut = true;

      try {
        // Dispatch logout action to clear redux state
        if (getStore && logoutAction) {
          const store = getStore();
          store.dispatch(logoutAction());
        }

        // Purge persisted data
        if (getPersistor) {
          const persistor = getPersistor();
          await persistor.purge();
        }

        // Clear any additional storage
        localStorage.removeItem('persist:root');

        // Redirect to login page
        window.location.href = '/login';
      } catch (logoutError) {
        console.error("Error during logout:", logoutError);
        // Force redirect even if logout fails
        window.location.href = '/login';
      } finally {
        // Reset flag after a delay to allow for any pending requests
        setTimeout(() => {
          isLoggingOut = false;
        }, 1000);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
